import React, { useEffect, useMemo, useState, createElement } from 'react';
import { useData } from '../../contexts/DataContext';
import { DownloadIcon, FileTextIcon, CheckIcon, ClipboardIcon } from 'lucide-react';
const ExportDataPage: React.FC = () => {
  const {
    readings,
    exportData
  } = useData();
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [exportPeriod, setExportPeriod] = useState<'all' | 'year' | 'month' | 'week'>('all');
  const [jsonPreview, setJsonPreview] = useState<string>('');
  const [copied, setCopied] = useState(false);
  // Filter readings by selected period
  const filteredReadings = useMemo(() => {
    const now = new Date();
    let filterStartDate: Date;
    switch (exportPeriod) {
      case 'week':
        filterStartDate = new Date(now);
        filterStartDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        filterStartDate = new Date(now);
        filterStartDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        filterStartDate = new Date(now);
        filterStartDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return readings;
    }
    return readings.filter(reading => new Date(reading.timestamp) >= filterStartDate);
  }, [readings, exportPeriod]);
  // Generate JSON preview
  useEffect(() => {
    if (exportFormat === 'json') {
      const previewData = filteredReadings.slice(0, 5).map(reading => ({
        timestamp: reading.timestamp,
        value: reading.value,
        unit: reading.unit
      }));
      setJsonPreview(JSON.stringify(previewData, null, 2));
    }
  }, [filteredReadings, exportFormat]);
  // Handle export
  const handleExport = () => {
    if (exportFormat === 'csv') {
      // Create CSV content
      const headers = 'Timestamp,Value (kWh)\n';
      const rows = filteredReadings.map(r => `${r.timestamp},${r.value}`).join('\n');
      const csvContent = `data:text/csv;charset=utf-8,${headers}${rows}`;
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `energy_readings_${exportPeriod}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      // Trigger download
      link.click();
      document.body.removeChild(link);
    } else {
      // Create JSON content
      const jsonContent = JSON.stringify(filteredReadings, null, 2);
      const blob = new Blob([jsonContent], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      // Create download link
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `energy_readings_${exportPeriod}_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      // Trigger download
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };
  // Copy JSON to clipboard
  const copyToClipboard = () => {
    const fullData = JSON.stringify(filteredReadings, null, 2);
    navigator.clipboard.writeText(fullData).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  return <div className="space-y-6" data-id="element-124">
      <h2 className="text-2xl font-bold" data-id="element-125">Export Data</h2>
      <div className="bg-white rounded-lg shadow p-4" data-id="element-126">
        <div className="flex items-center mb-4" data-id="element-127">
          <FileTextIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-128" />
          <h3 className="text-lg font-semibold" data-id="element-129">Export Options</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-130">
          <div data-id="element-131">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-132">
              Export Format
            </h4>
            <div className="flex space-x-2" data-id="element-133">
              <button onClick={() => setExportFormat('csv')} className={`px-4 py-2 rounded-md ${exportFormat === 'csv' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-134">
                CSV Format
              </button>
              <button onClick={() => setExportFormat('json')} className={`px-4 py-2 rounded-md ${exportFormat === 'json' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-135">
                JSON Format
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2" data-id="element-136">
              CSV is compatible with Excel and other spreadsheet applications.
              JSON is better for programmatic access.
            </p>
          </div>
          <div data-id="element-137">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-138">
              Time Period
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-139">
              <button onClick={() => setExportPeriod('all')} className={`px-4 py-2 rounded-md ${exportPeriod === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-140">
                All Data
              </button>
              <button onClick={() => setExportPeriod('year')} className={`px-4 py-2 rounded-md ${exportPeriod === 'year' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-141">
                Last 12 Months
              </button>
              <button onClick={() => setExportPeriod('month')} className={`px-4 py-2 rounded-md ${exportPeriod === 'month' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-142">
                Last 30 Days
              </button>
              <button onClick={() => setExportPeriod('week')} className={`px-4 py-2 rounded-md ${exportPeriod === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-143">
                Last 7 Days
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2" data-id="element-144">
              {filteredReadings.length} readings will be exported
            </p>
          </div>
        </div>
        <div className="mt-6" data-id="element-145">
          <button onClick={handleExport} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" data-id="element-146">
            <DownloadIcon className="h-5 w-5 mr-2" data-id="element-147" />
            Export Data
          </button>
        </div>
      </div>
      {/* JSON Preview (only show if JSON format is selected) */}
      {exportFormat === 'json' && <div className="bg-white rounded-lg shadow p-4" data-id="element-148">
          <div className="flex justify-between items-center mb-4" data-id="element-149">
            <h3 className="text-lg font-semibold" data-id="element-150">
              JSON Preview (first 5 records)
            </h3>
            <button onClick={copyToClipboard} className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors" data-id="element-151">
              {copied ? <>
                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-152" />
                  Copied!
                </> : <>
                  <ClipboardIcon className="h-4 w-4 mr-1" data-id="element-153" />
                  Copy All
                </>}
            </button>
          </div>
          <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-154">
            <pre className="text-sm text-gray-800" data-id="element-155">{jsonPreview}</pre>
          </div>
          <p className="text-sm text-gray-500 mt-2" data-id="element-156">
            The full export will include all {filteredReadings.length} readings.
          </p>
        </div>}
      {/* Data Summary */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-157">
        <h3 className="text-lg font-semibold mb-4" data-id="element-158">Data Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-159">
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-160">
            <div className="text-xl font-bold text-blue-700" data-id="element-161">
              {readings.length}
            </div>
            <div className="text-sm text-gray-600" data-id="element-162">Total Readings</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-163">
            <div className="text-xl font-bold text-green-700" data-id="element-164">
              {new Date(readings[0]?.timestamp).toLocaleDateString() || 'N/A'}
            </div>
            <div className="text-sm text-gray-600" data-id="element-165">First Reading Date</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg" data-id="element-166">
            <div className="text-xl font-bold text-purple-700" data-id="element-167">
              {new Date(readings[readings.length - 1]?.timestamp).toLocaleDateString() || 'N/A'}
            </div>
            <div className="text-sm text-gray-600" data-id="element-168">Latest Reading Date</div>
          </div>
        </div>
      </div>
    </div>;
};
export default ExportDataPage;